/* =============================================================================
 *  Kreux Matrix — Site-wide i18n dictionary (EN / DE)
 *
 *  German translations are crafted to mirror the luxury, premium, elite
 *  enterprise tone of the English original. Trademarks (Kreux Matrix,
 *  Responsive Intelligence™, Kreux LOGIC™) remain untranslated by design.
 * =============================================================================
 */

export const STRINGS = {
  /* =========================== ENGLISH ================================= */
  en: {
    nav: {
      platform: "Platform",
      intelligence: "Intelligence",
      manifesto: "Manifesto",
      contact: "Contact",
      langLabel: "Language",
      langEN: "EN",
      langDE: "DE",
    },
    hero: {
      eyebrow: "Kreux Matrix — Systems That Think and Speak",
      headline1pre: "The Future",
      headline1em: "Doesn’t\u00A0Run.",
      headline2pre: "It",
      headline2cyan: "Responds.",
      sub: {
        prefix:
          "Kreux Matrix unifies AI automation, adaptive workflows, and lifelike voice agents into a single operational system. We engineer ",
        mark: "Responsive Intelligence™",
        suffix:
          "\u00A0— technology that doesn’t just process tasks, but understands, communicates, and executes with absolute purpose.",
      },
      primaryCTA: "Experience The Intelligence",
    },
    section1: {
      eyebrow: "THE ARCHITECTURE OF EXCELLENCE",
      titlePre: "One System.",
      titleEm: "Infinite",
      titleSuffix: "Operational Velocity.",
      body:
        "We do not build simple software tools. We architect unified intelligence. Kreux Matrix replaces fragmented operations with an elite, interconnected ecosystem designed to communicate seamlessly, execute flawlessly, and elevate your entire enterprise.",
      dossier: "DOSSIER",
      openDossier: "Open Core Dossier",
      experienceCores: "Experience The Cores",
      experienceCore1: "Experience The Core I",
      experienceCore2: "Experience The Core II",
      privateConsult: "Request Private Consultation",
    },
    cores: {
      voice: {
        sub: "Automated Phone & Voice Operations",
        surface: "Conversational Elegance. Zero Latency.",
        deep:
          "Re-engineer how your business speaks. Voice deploys stunningly lifelike verbal engines that manage complex customer dialogue and outbound phone operations. Free from robotic delays, it delivers an elite brand experience 24 hours a day.",
        benefit:
          "Perfect for organizations facing massive call volumes who refuse to compromise on elite client care.",
      },
      logic: {
        sub: "Digital Data & Workflow Sync",
        surface: "Invisible Orchestration. Flawless Execution.",
        deep:
          "Eradicate the friction of manual digital labor. Logic operates silently in the background of your enterprise—reading complex data, syncing disparate systems, and executing multi-step operations instantly.",
        benefit:
          "Designed for businesses drowning in administrative data entry, invoices, and cross-platform management.",
      },
      sovereign: {
        sub: "Bespoke Private Systems",
        surface: "Proprietary Architecture. Absolute Independence.",
        deep:
          "Reserved for enterprises requiring a completely unique competitive edge. Sovereign connects your leadership directly with our elite engineering team to design, train, and deploy private models built entirely from the ground up.",
        benefit:
          "Reserved for corporations requiring bespoke, highly secure, and entirely proprietary assets. By private consultation only.",
      },
    },
    modal: {
      deepLabel: "DEEP UTILITY DETAILS",
      benefitLabel: "CLEAR CORPORATE BENEFIT",
      close: "CLOSE",
      runSim: "Run Core II Logic Simulator",
    },
    section3: {
      eyebrow: "SECTION\u00A003 / THE SIDE-BY-SIDE SPEEDRUN",
      title1: "12 Minutes",
      titleVs: "vs",
      title2: "1.4 Seconds.",
      oldWayTag: "THE OLD WAY",
      newWayTag: "THE KREUX MATRIX WAY",
      manualOperator: "MANUAL OPERATOR",
      manualLabel: "Manual Labor - 12 Minutes",
      autoEngine: "AUTONOMOUS ENGINE",
      autoLabel: "Kreux LOGIC Engine - 1.4 Seconds",
      alertTag: "PREMIUM INTERACTIVE OPPORTUNITY",
      alertPrompt: "“Would you like to have a real-time experience?”",
      alertYes: "YES",
      alertNo: "NO",
    },
    section2: {
      eyebrow: "SECTION\u00A002 / THE INVISIBLE STREAM",
      titleSource: "Source",
      titleEngine: "Kreux Logic",
      titleDestinations: "Destinations",
      col1Tag: "COLUMN\u00A001 / THE INPUT",
      col1Heading: "Unstructured Data Inflow",
      col1IncomingPre: "Incoming:",
      col2Tag: "COLUMN\u00A002 / THE ENGINE",
      col2Idle: "System Idle. Awaiting Trigger.",
      col2Live: "SYSTEM LIVE",
      col3Tag: "COLUMN\u00A003 / THE ECOSYSTEM",
      col3Heading: "Ecosystem Mapping",
      statusPre: "Status:",
      statusOutOfSync: "Out of Sync.",
      statusSyncing: "Syncing…",
      statusOrchestrated: "Fully Orchestrated.",
      ecoPending: "— awaiting orchestration",
      dest: {
        crm: "[Updated] Account upgraded to Enterprise.",
        erp: "[Updated] 500 licenses provisioned automatically.",
        finance: "[Generated] Invoice #9910 created and routed.",
        legal: "[Archived] Securely stored in encrypted matrix repository.",
      },
      scoreSim: "SIMULATION",
      scoreSimVal: "Successful.",
      scoreTime: "TOTAL EXECUTION TIME",
      scoreTimeVal: "0.82 Seconds.",
      scoreHuman: "HUMAN INTERVENTION",
      scoreHumanVal: "0%.",
      scoreErr: "ERRORS DETECTED",
      scoreErrVal: "None.",
      runBtn: "Run Live Simulation",
      runBtnRunning: "Simulation Running…",
      logs: {
        ingest: "[LOGIC]: Ingesting Client_Contract_772.pdf...",
        scan: "[LOGIC]: Scanning 42 pages of unformatted text...",
        extract: "[LOGIC]: Extracting terms: Enterprise Tier | $120,000 ARR | Net 30",
        compliance: "[LOGIC]: Matching compliance clauses with regional laws... [Passed]",
        formatting: "[LOGIC]: Formatting system data models... [Complete]",
      },
    },
    section4: {
      eyebrow: "SECTION\u00A004 / TACTILE SIMULATOR",
      title: "Feel The Throughput.",
      nodes: {
        email: { label: "EMAIL", caption: "Envelope ingress" },
        db: { label: "DATABASE", caption: "Core data node" },
        invoice: { label: "INVOICE", caption: "Document egress" },
      },
      trigger: "Trigger Test Workflow",
      triggerRunning: "Triggering Workflow…",
      success:
        "“Workflow Complete: 1,420 data points read, verified, and securely synced across 3 platforms in 0.8 seconds.”",
    },
    consult: {
      tagSuffix: "ENGLISH",
      title: "Speak Directly With Our Architects.",
      close: "CLOSE",
      success:
        "Request received. Redirecting you to the secure Kreux Matrix Calendly portal…",
      nameLabel: "Full Name",
      namePh: "Your full name",
      companyLabel: "Company Name",
      companyPh: "Your organisation",
      emailLabel: "Corporate Email",
      emailPh: "name@your-company.com",
      phoneLabel: "Phone Number",
      phonePh: "Phone number",
      briefLabel: "Why do you need a personal consultation?",
      briefPh:
        "Share the operational challenges you’d like our architects to engage with.",
      cancel: "Cancel",
      submit: "Submit Request",
      err: {
        required: "Required.",
        brief: "Please share at least a couple of sentences.",
        emailInvalid: "Enter a valid corporate email address.",
        emailPublic:
          "Public domains aren’t accepted. Please use your corporate email.",
        phoneInvalid: "Enter a valid phone number.",
      },
    },
    voice: {
      title: "Aria — Digital Call Assistant",
      sub: "Pre-recorded playback · synced subtitles",
      chooseDomain: "Choose a domain",
      restaurant: "Restaurant & Catering",
      tourism: "Tourism & Hospitality",
      healthcare: "Healthcare & Medical",
      pickScenario: "Select a scenario",
      call: "Call the Assistant",
      connecting: "Establishing secure voice link…",
      live: "LIVE · SECURE LINE",
      assistant: "Aria",
      caller: "Caller",
      cont: "Continue",
      quit: "End Call",
    },
    arch: {
      tag: "KREUX ARCHITECTURE",
      title: "Kreux Architecture",
      sub: "Connect with Us",
      close: "Close",
      nameLabel: "Name",
      namePh: "Your full name",
      companyLabel: "Company",
      companyPh: "Your organisation",
      emailLabel: "Email",
      emailPh: "name@your-company.com",
      categoryLabel: "Category",
      categoryPh: "Select a category",
      phoneLabel: "Phone",
      phonePh: "+1 555 555 5555",
      submit: "Connect",
      submitting: "Securing transmission…",
      success: "Transmission secured. Our architectural team will initiate contact shortly.",
      cat: {
        voice: "Voice & Conversational AI",
        logic: "Workflow & Automation",
        sovereign: "Bespoke Private System",
        other: "Other / Exploratory",
      },
      err: {
        required: "Required.",
        email: "Enter a valid email address.",
        phone: "Enter a valid phone number.",
      },
    },
  },

  /* ============================ GERMAN ================================= */
  de: {
    nav: {
      platform: "Plattform",
      intelligence: "Intelligenz",
      manifesto: "Manifest",
      contact: "Kontakt",
      langLabel: "Sprache",
      langEN: "EN",
      langDE: "DE",
    },
    hero: {
      eyebrow: "Kreux Matrix — Systeme, die denken und sprechen",
      headline1pre: "Die Zukunft",
      headline1em: "Läuft\u00A0nicht.",
      headline2pre: "Sie",
      headline2cyan: "Antwortet.",
      sub: {
        prefix:
          "Kreux Matrix vereint KI-Automatisierung, adaptive Arbeitsabläufe und lebensechte Sprachassistenten in einem einzigen operativen System. Wir entwickeln ",
        mark: "Responsive Intelligence™",
        suffix:
          "\u00A0— Technologie, die nicht nur Aufgaben verarbeitet, sondern versteht, kommuniziert und mit absoluter Zielstrebigkeit handelt.",
      },
      primaryCTA: "Die Intelligenz erleben",
    },
    section1: {
      eyebrow: "DIE ARCHITEKTUR DER EXZELLENZ",
      titlePre: "Ein System.",
      titleEm: "Grenzenlose",
      titleSuffix: "operative Geschwindigkeit.",
      body:
        "Wir entwickeln keine simplen Software-Werkzeuge. Wir gestalten vereinheitlichte Intelligenz. Kreux Matrix ersetzt fragmentierte Abläufe durch ein elitäres, vernetztes Ökosystem — konzipiert für nahtlose Kommunikation, makellose Ausführung und die Erhebung Ihres gesamten Unternehmens.",
      dossier: "DOSSIER",
      openDossier: "Kern-Dossier öffnen",
      experienceCores: "Die Kerne erleben",
      experienceCore1: "Kern I erleben",
      experienceCore2: "Kern II erleben",
      privateConsult: "Private Beratung anfragen",
    },
    cores: {
      voice: {
        sub: "Automatisierte Telefon- und Sprachoperationen",
        surface: "Konversationelle Eleganz. Null Latenz.",
        deep:
          "Definieren Sie neu, wie Ihr Unternehmen spricht. Voice setzt erstaunlich lebensechte Sprachmodelle ein, die komplexe Kundendialoge und ausgehende Telefonoperationen steuern. Frei von roboterhaften Verzögerungen liefert es 24 Stunden am Tag ein erstklassiges Markenerlebnis.",
        benefit:
          "Ideal für Organisationen mit hohem Anrufvolumen, die bei der Betreuung anspruchsvoller Kunden keine Kompromisse eingehen.",
      },
      logic: {
        sub: "Digitale Daten- und Workflow-Synchronisation",
        surface: "Unsichtbare Orchestrierung. Makellose Ausführung.",
        deep:
          "Eliminieren Sie die Reibung manueller digitaler Arbeit. Logic agiert lautlos im Hintergrund Ihres Unternehmens — liest komplexe Daten, synchronisiert disparate Systeme und führt mehrstufige Operationen sofort aus.",
        benefit:
          "Konzipiert für Unternehmen, die in administrativer Dateneingabe, Rechnungen und plattformübergreifender Verwaltung versinken.",
      },
      sovereign: {
        sub: "Maßgeschneiderte Privatsysteme",
        surface: "Eigene Architektur. Absolute Unabhängigkeit.",
        deep:
          "Reserviert für Unternehmen, die einen vollständig einzigartigen Wettbewerbsvorteil benötigen. Sovereign verbindet Ihre Führungsebene direkt mit unserem Elite-Engineering-Team, um private Modelle vollständig von Grund auf zu entwerfen, zu trainieren und einzusetzen.",
        benefit:
          "Vorbehalten Konzernen, die maßgeschneiderte, hochsichere und vollständig proprietäre Assets benötigen. Ausschließlich nach privater Beratung.",
      },
    },
    modal: {
      deepLabel: "DETAILS ZUR FUNKTIONSWEISE",
      benefitLabel: "KLARER UNTERNEHMENSVORTEIL",
      close: "SCHLIESSEN",
      runSim: "Logic-Simulator (Kern II) starten",
    },
    section3: {
      eyebrow: "ABSCHNITT\u00A003 / DER DIREKTVERGLEICH",
      title1: "12 Minuten",
      titleVs: "vs.",
      title2: "1,4 Sekunden.",
      oldWayTag: "DER ALTE WEG",
      newWayTag: "DER KREUX MATRIX WEG",
      manualOperator: "MANUELLER OPERATOR",
      manualLabel: "Manuelle Arbeit – 12 Minuten",
      autoEngine: "AUTONOMER MOTOR",
      autoLabel: "Kreux LOGIC Engine – 1,4 Sekunden",
      alertTag: "EXKLUSIVE INTERAKTIVE GELEGENHEIT",
      alertPrompt: "„Möchten Sie ein Echtzeit-Erlebnis erleben?“",
      alertYes: "JA",
      alertNo: "NEIN",
    },
    section2: {
      eyebrow: "ABSCHNITT\u00A002 / DER UNSICHTBARE STROM",
      titleSource: "Quelle",
      titleEngine: "Kreux Logic",
      titleDestinations: "Ziele",
      col1Tag: "SPALTE\u00A001 / DER INPUT",
      col1Heading: "Unstrukturierter Datenzufluss",
      col1IncomingPre: "Eingehend:",
      col2Tag: "SPALTE\u00A002 / DER MOTOR",
      col2Idle: "System inaktiv. Warte auf Auslöser.",
      col2Live: "SYSTEM LIVE",
      col3Tag: "SPALTE\u00A003 / DAS ÖKOSYSTEM",
      col3Heading: "Ökosystem-Mapping",
      statusPre: "Status:",
      statusOutOfSync: "Nicht synchronisiert.",
      statusSyncing: "Synchronisiere…",
      statusOrchestrated: "Vollständig orchestriert.",
      ecoPending: "— wartet auf Orchestrierung",
      dest: {
        crm: "[Aktualisiert] Konto auf Enterprise hochgestuft.",
        erp: "[Aktualisiert] 500 Lizenzen automatisch bereitgestellt.",
        finance: "[Erstellt] Rechnung #9910 erzeugt und weitergeleitet.",
        legal: "[Archiviert] Sicher im verschlüsselten Matrix-Repository abgelegt.",
      },
      scoreSim: "SIMULATION",
      scoreSimVal: "Erfolgreich.",
      scoreTime: "GESAMT-AUSFÜHRUNGSZEIT",
      scoreTimeVal: "0,82 Sekunden.",
      scoreHuman: "MENSCHLICHER EINGRIFF",
      scoreHumanVal: "0 %.",
      scoreErr: "ERKANNTE FEHLER",
      scoreErrVal: "Keine.",
      runBtn: "Live-Simulation starten",
      runBtnRunning: "Simulation läuft…",
      logs: {
        ingest: "[LOGIC]: Importiere Client_Contract_772.pdf...",
        scan: "[LOGIC]: Analysiere 42 Seiten unformatierten Text...",
        extract: "[LOGIC]: Extrahiere Bedingungen: Enterprise-Tarif | 120.000 $ ARR | Net 30",
        compliance: "[LOGIC]: Gleiche Compliance-Klauseln mit regionalem Recht ab... [Bestanden]",
        formatting: "[LOGIC]: Formatiere System-Datenmodelle... [Abgeschlossen]",
      },
    },
    section4: {
      eyebrow: "ABSCHNITT\u00A004 / TAKTILER SIMULATOR",
      title: "Spüren Sie den Durchsatz.",
      nodes: {
        email: { label: "E-MAIL", caption: "Eingang" },
        db: { label: "DATENBANK", caption: "Kerndaten-Knoten" },
        invoice: { label: "RECHNUNG", caption: "Dokumentausgang" },
      },
      trigger: "Test-Workflow auslösen",
      triggerRunning: "Workflow wird ausgelöst…",
      success:
        "„Workflow abgeschlossen: 1.420 Datenpunkte gelesen, verifiziert und in 0,8 Sekunden sicher über 3 Plattformen synchronisiert.“",
    },
    consult: {
      tagSuffix: "DEUTSCH",
      title: "Sprechen Sie direkt mit unseren Architekten.",
      close: "SCHLIESSEN",
      success:
        "Anfrage erhalten. Sie werden zum sicheren Kreux-Matrix Calendly-Portal weitergeleitet…",
      nameLabel: "Vollständiger Name",
      namePh: "Ihr vollständiger Name",
      companyLabel: "Firmenname",
      companyPh: "Ihre Organisation",
      emailLabel: "Geschäftliche E-Mail",
      emailPh: "name@ihr-unternehmen.com",
      phoneLabel: "Telefonnummer",
      phonePh: "Telefonnummer",
      briefLabel: "Warum benötigen Sie eine persönliche Beratung?",
      briefPh:
        "Schildern Sie die operativen Herausforderungen, mit denen sich unsere Architekten befassen sollen.",
      cancel: "Abbrechen",
      submit: "Anfrage absenden",
      err: {
        required: "Pflichtfeld.",
        brief: "Bitte teilen Sie mindestens ein paar Sätze mit.",
        emailInvalid: "Geben Sie eine gültige geschäftliche E-Mail-Adresse ein.",
        emailPublic:
          "Öffentliche Domains werden nicht akzeptiert. Bitte verwenden Sie Ihre Firmen-E-Mail.",
        phoneInvalid: "Geben Sie eine gültige Telefonnummer ein.",
      },
    },
    voice: {
      title: "Aria — Digitale Anrufassistentin",
      sub: "Vorab aufgezeichnete Wiedergabe · synchronisierte Untertitel",
      chooseDomain: "Wählen Sie eine Branche",
      restaurant: "Restaurant & Catering",
      tourism: "Tourismus & Gastgewerbe",
      healthcare: "Gesundheit & Medizin",
      pickScenario: "Szenario auswählen",
      call: "Assistent anrufen",
      connecting: "Sichere Sprachverbindung wird aufgebaut…",
      live: "LIVE · SICHERE LEITUNG",
      assistant: "Aria",
      caller: "Anrufer",
      cont: "Weiter",
      quit: "Anruf beenden",
    },
    arch: {
      tag: "KREUX ARCHITECTURE",
      title: "Kreux Architecture",
      sub: "Nehmen Sie Kontakt zu uns auf",
      close: "Schließen",
      nameLabel: "Name",
      namePh: "Ihr vollständiger Name",
      companyLabel: "Unternehmen",
      companyPh: "Ihre Organisation",
      emailLabel: "E-Mail",
      emailPh: "name@ihr-unternehmen.com",
      categoryLabel: "Kategorie",
      categoryPh: "Kategorie auswählen",
      phoneLabel: "Telefon",
      phonePh: "+49 30 555 555 55",
      submit: "Verbinden",
      submitting: "Übertragung wird gesichert…",
      success: "Übertragung gesichert. Unser Architektur-Team wird in Kürze Kontakt aufnehmen.",
      cat: {
        voice: "Sprach- und Konversations-KI",
        logic: "Workflow & Automatisierung",
        sovereign: "Maßgeschneidertes Privatsystem",
        other: "Sonstiges / Sondierung",
      },
      err: {
        required: "Pflichtfeld.",
        email: "Geben Sie eine gültige E-Mail-Adresse ein.",
        phone: "Geben Sie eine gültige Telefonnummer ein.",
      },
    },
  },
};
